import { sanitizeVisitorName } from '@orbomb/protocol';
import type { Camp } from '@orbomb/sim';
import {
  authRedirectError,
  looksLikeEmail,
  normalizeRoomCode,
  roomCodeFromSearch,
  roomLink,
  sanitizeDisplayName,
} from '../online/codes';
import {
  isClientSupabaseKey,
  isSupabaseConfigured,
  loadStoredSupabase,
  resolveSupabaseConfig,
  saveStoredSupabase,
  type SupabaseConfig,
} from '../online/config';
import { readEnvSupabase } from '../online/env';
import type { LobbyApi } from '../online/lobbyApi';
import { campFor, seatName, type Account, type MyMatch, type OnlineRoom } from '../online/parse';
import { readColyseusUrl } from '../online/colyseus';
import {
  clearResumeSeat,
  fetchSeatStatus,
  fetchServerResumeSeat,
  loadResumeSeat,
  resolvePlayingEntry,
  type ResumeSeat,
} from '../online/resume';
import { onlineError, OnlineService } from '../online/service';
import { createVisitor, loadVisitor, saveVisitor, type Visitor } from '../online/visitor';
import { VisitorLobby } from '../online/visitorLobby';

/** Whoever plays online from this screen: the account first, else the visitor. */
interface Player {
  id: string;
  visitor: boolean;
}

const ROOM_HINT = 'Create a room, or join one from the list.';
const GUEST_HINT = 'Playing as a guest: scores are not saved. Sign in to keep them.';
const GUEST_LEAD = 'No account needed: pick a name and play, online included.';

export class OnlineUi {
  private service: OnlineService | null = null;
  private account: Account | null = null;
  /** Name-only player kept on this device. An account, when signed in, wins. */
  private visitor: Visitor | null = null;
  private visitorLobby: VisitorLobby | null = null;
  /** Name typed on the sign-in screen while the guest pop-up is open. */
  private pendingGuestName: string | null = null;
  private room: OnlineRoom | null = null;
  private unsubAuth: (() => void) | null = null;
  private unsubList: (() => void) | null = null;
  private unsubLobby: (() => void) | null = null;
  private poll = 0;
  private lobbyPoll = 0;
  private applied = '';
  private starting = false;
  private matchStarted = false;
  private followedLink = false;
  private triedResume = false;
  private sawWaiting = false;
  /** In-flight `leave_room` so rematch can await the same leave as `returnToMenu`. */
  private leaving: Promise<void> | null = null;
  /** Consecutive Find/Create hits when the match server is unreachable. */
  private unknownSeatAttempts = 0;

  private readonly settingsEl = document.getElementById('settings') as HTMLElement;
  private readonly auth = document.getElementById('auth') as HTMLElement;
  private readonly lobby = document.getElementById('lobby') as HTMLElement;
  private readonly hint = document.getElementById('online-hint') as HTMLElement;
  private readonly guestPane = document.getElementById('auth-guest') as HTMLElement;
  private readonly guestLead = document.getElementById('guest-lead') as HTMLElement;
  private readonly guestNameInput = document.getElementById('guest-name') as HTMLInputElement;
  private readonly guestError = document.getElementById('guest-error') as HTMLElement;
  private readonly guestNotice = document.getElementById('guest-notice') as HTMLDialogElement;
  private readonly guestNoticeName = document.getElementById('guest-notice-name') as HTMLElement;
  private readonly signinPane = document.getElementById('auth-signin') as HTMLElement;
  private readonly signupPane = document.getElementById('auth-signup') as HTMLElement;
  private readonly signinError = document.getElementById('auth-signin-error') as HTMLElement;
  private readonly signupError = document.getElementById('auth-signup-error') as HTMLElement;
  private readonly accountLabel = document.getElementById('account-label') as HTMLElement;
  private readonly authButton = document.getElementById('btn-auth') as HTMLButtonElement;
  private readonly loginIdInput = document.getElementById('auth-login-id') as HTMLInputElement;
  private readonly loginPasswordInput = document.getElementById(
    'auth-login-password',
  ) as HTMLInputElement;
  private readonly nameInput = document.getElementById('auth-name') as HTMLInputElement;
  private readonly emailInput = document.getElementById('auth-email') as HTMLInputElement;
  private readonly passwordInput = document.getElementById('auth-password') as HTMLInputElement;
  private readonly urlInput = document.getElementById('set-supabase-url') as HTMLInputElement;
  private readonly keyInput = document.getElementById('set-supabase-key') as HTMLInputElement;
  private readonly supabaseNote = document.getElementById('supabase-note') as HTMLElement;
  private readonly lobbyCode = document.getElementById('lobby-code') as HTMLElement;
  private readonly lobbyStatus = document.getElementById('lobby-status') as HTMLElement;

  constructor(
    private readonly handlers: {
      onStartMatch: (
        room: OnlineRoom,
        camp: Camp,
        lobby: LobbyApi,
        resumeToken?: string | null,
      ) => void;
      onLeaveMatch: () => void;
      /** Drop LiveMatch without returning to the home screen (rematch). */
      onDisposeMatch: () => void;
      /** A live online match. A new Supabase project leaves it. */
      inMatch: () => boolean;
      /** Any match on screen, Single play and Watch too. The session check must not cover it. */
      matchOnScreen: () => boolean;
      showGate: () => void;
      showMenu: () => void;
      showMulti: () => void;
      showLobby: () => void;
    },
  ) {}

  isSignedIn(): boolean {
    return this.account !== null;
  }

  /** An account or a visitor: enough to open the multiplayer screen. */
  hasPlayer(): boolean {
    return this.player() !== null;
  }

  /** The sign-in screen on the name field: the quick way online. */
  askForName(focus = true): void {
    this.handlers.showGate();
    this.guestError.textContent = '';
    const code = this.player() ? null : roomCodeFromSearch(window.location.search);
    this.guestLead.textContent = code ? `Pick a name to join room ${code}.` : GUEST_LEAD;
    if (this.visitor) this.guestNameInput.value = this.visitor.name;
    if (focus) this.guestNameInput.focus();
  }

  bind(): void {
    this.visitor = loadVisitor();
    this.visitorLobby = this.visitor ? new VisitorLobby(readColyseusUrl(), this.visitor) : null;
    const redirectError = authRedirectError(window.location.hash);
    if (redirectError) {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(null, '', url);
      this.signinError.textContent = redirectError;
    }
    this.authButton.addEventListener('click', () => {
      if (this.account) {
        void this.logout();
        return;
      }
      this.askForName(false);
    });
    document.getElementById('btn-guest')?.addEventListener('click', () => this.pickGuestName());
    document
      .getElementById('btn-guest-confirm')
      ?.addEventListener('click', () => void this.playAsGuest());
    document
      .getElementById('btn-guest-signup')
      ?.addEventListener('click', () => this.signUpInstead());
    document.getElementById('btn-signup')?.addEventListener('click', () => void this.signup());
    document.getElementById('btn-login')?.addEventListener('click', () => void this.login());
    document.getElementById('btn-goto-signup')?.addEventListener('click', () => {
      this.showAuthPane('signup');
    });
    document.getElementById('btn-goto-signin')?.addEventListener('click', () => {
      this.showAuthPane('signin');
    });
    document
      .getElementById('btn-create-online')
      ?.addEventListener('click', () => void this.create());
    document.getElementById('btn-find-online')?.addEventListener('click', () => void this.find());
    document.getElementById('join-code-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      document.getElementById('btn-join-code')?.click();
    });
    document.getElementById('btn-join-code')?.addEventListener('click', () => {
      const code = normalizeRoomCode(
        (document.getElementById('join-code') as HTMLInputElement | null)?.value ?? '',
      );
      if (!code) {
        this.hint.textContent = 'Enter a 4-letter room code.';
        return;
      }
      void this.openCode(code);
    });
    document.getElementById('btn-lobby-leave')?.addEventListener('click', () => {
      if (this.matchStarted) this.handlers.onLeaveMatch();
      else void this.abandon();
    });
    document.getElementById('btn-copy-room')?.addEventListener('click', () => void this.copyLink());
    document.getElementById('btn-supabase-reset')?.addEventListener('click', () => {
      saveStoredSupabase({ url: '', publishableKey: '' });
      this.fillSupabaseFields();
      this.apply(resolveSupabaseConfig(loadStoredSupabase(), readEnvSupabase()));
    });
    this.urlInput.addEventListener('change', () => this.commitSupabase());
    this.keyInput.addEventListener('change', () => this.commitSupabase());
    this.guestPane.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.pickGuestName();
    });
    this.signinPane.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      void this.login();
    });
    this.signupPane.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      void this.signup();
    });
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!this.auth.hidden) return;
      if (!this.lobby.hidden && !this.matchStarted) void this.abandon();
    });
    this.fillSupabaseFields();
    this.apply(resolveSupabaseConfig(loadStoredSupabase(), readEnvSupabase()));
  }

  fillSupabaseFields(): void {
    const config = resolveSupabaseConfig(loadStoredSupabase(), readEnvSupabase());
    this.urlInput.value = config.url;
    this.keyInput.value = config.publishableKey;
    this.supabaseNote.textContent = isSupabaseConfigured(config)
      ? 'Connected settings are saved in this browser. The publishable key is public; the database rules protect scores.'
      : 'Paste the project URL and the publishable key from Supabase → Project Settings → API. Then run pnpm migrate.';
  }

  /** Drops the server room without changing which screen is open. */
  async releaseRoom(): Promise<void> {
    if (this.leaving) {
      await this.leaving;
      return;
    }
    const id = this.room?.id ?? null;
    const lobby = this.lobbyApi();
    const clearResume = Boolean(id) || this.matchStarted;
    this.matchStarted = false;
    this.starting = false;
    this.sawWaiting = false;
    this.room = null;
    this.clearLobbyWatch();
    this.clearRoomQuery();
    // Do not wipe another tab's resume token when this tab has no lobby row.
    if (clearResume) clearResumeSeat();
    this.leaving = (async () => {
      if (id && lobby) {
        try {
          await lobby.leaveRoom(id);
        } catch (error) {
          this.hint.textContent = onlineError(error);
        }
      }
      if (this.player()) void this.refreshLists();
    })();
    try {
      await this.leaving;
    } finally {
      this.leaving = null;
    }
  }

  /**
   * After an online match, leave the old lobby row then create a new one.
   * Do not flash the home screen between matches.
   */
  async requestRematch(): Promise<void> {
    clearResumeSeat();
    this.handlers.onDisposeMatch();
    this.hint.textContent = 'Opening a new room…';
    await this.releaseRoom();
    await this.create();
  }

  /** Leaves the current room, if any, and returns to the menu or the sign-in screen. */
  async abandon(): Promise<void> {
    await this.releaseRoom();
    this.presentAccountScreen();
  }

  /**
   * The seat could not be taken (match server unreachable, join refused).
   * Drop the lobby row like Home does, but land on the multiplayer screen
   * with the reason so the player can fix the server address and retry.
   */
  async abortMatch(message: string): Promise<void> {
    clearResumeSeat();
    this.hint.textContent = message;
    if (this.player()) this.handlers.showMulti();
    else this.handlers.showGate();
    await this.releaseRoom();
  }

  private commitSupabase(): void {
    const typedKey = this.keyInput.value.trim();
    const saved = {
      url: this.urlInput.value.trim() ? this.urlInput.value : '',
      publishableKey: typedKey,
    };
    saveStoredSupabase(saved);
    const resolved = resolveSupabaseConfig(loadStoredSupabase(), readEnvSupabase());
    if ((this.urlInput.value.trim() || typedKey) && !isSupabaseConfigured(resolved)) {
      this.supabaseNote.textContent =
        typedKey && !isClientSupabaseKey(typedKey)
          ? 'Use the publishable key from API settings, not the secret key.'
          : 'The project URL needs to look like https://abcd.supabase.co and the publishable key must be set.';
      return;
    }
    this.fillSupabaseFields();
    this.apply(resolved);
  }

  private apply(config: SupabaseConfig): void {
    const key = `${config.url}\n${config.publishableKey}`;
    if (key === this.applied) return;
    this.applied = key;
    if (this.handlers.inMatch() || this.room) this.handlers.onLeaveMatch();
    this.clearLobbyWatch();
    this.unsubAuth?.();
    this.unsubList?.();
    this.unsubAuth = null;
    this.unsubList = null;
    this.stopPoll();
    this.service?.dispose();
    this.service = OnlineService.connect(config);
    this.account = null;
    this.room = null;
    this.matchStarted = false;
    this.sawWaiting = false;
    this.paintAccount();
    this.paintRooms([]);
    this.paintResults([]);
    // A visitor only needs the match server, so the lists refresh without Supabase too.
    this.poll = window.setInterval(() => {
      void this.refreshLists();
    }, 8000);
    if (!this.service) {
      this.hint.textContent = this.visitor
        ? GUEST_HINT
        : 'Add your Supabase project in Settings to play online.';
      if (!this.handlers.matchOnScreen()) this.landWithoutAccount();
      this.visitorReady();
      return;
    }
    this.hint.textContent = this.visitor ? GUEST_HINT : ROOM_HINT;
    let authReady = false;
    let sessionChecked = false;
    const paintAuth = (account: Account | null, fromSessionCheck = false) => {
      if (fromSessionCheck) sessionChecked = true;
      if (!sessionChecked && !account) return;
      const previous = this.account?.id ?? null;
      this.account = account;
      this.paintAccount();
      const next = account?.id ?? null;
      const first = !authReady;
      authReady = true;
      if (!first && next === previous) {
        if (account) void this.refreshLists();
        return;
      }
      if (!account) {
        this.paintRooms([]);
        this.paintResults([]);
        this.hint.textContent = this.visitor ? GUEST_HINT : ROOM_HINT;
        if (!this.handlers.matchOnScreen() && this.settingsEl.hidden && this.lobby.hidden) {
          this.landWithoutAccount();
        }
        this.visitorReady();
        return;
      }
      this.hint.textContent = ROOM_HINT;
      if (!this.handlers.matchOnScreen() && !this.room && this.settingsEl.hidden) {
        this.handlers.showMenu();
      }
      void this.refreshLists();
      this.followLink();
    };
    this.unsubAuth = this.service.onAuth((account) => paintAuth(account));
    void this.service
      .reloadAccount()
      .then((account) => paintAuth(account, true))
      .catch(() => paintAuth(null, true));
    this.unsubList = this.service.watchRooms(() => {
      void this.refreshLists();
    });
  }

  /** No account after the session check: home, or the name field for a `?room=` link. */
  private landWithoutAccount(): void {
    if (!this.visitor && roomCodeFromSearch(window.location.search)) this.askForName();
    else this.handlers.showMenu();
  }

  /** A stored visitor plays once the session check found no account. */
  private visitorReady(): void {
    if (!this.visitor || this.account) return;
    void this.refreshLists();
    this.followLink();
  }

  private followLink(): void {
    if (!this.player() || this.handlers.inMatch()) return;
    if (!this.triedResume) {
      this.triedResume = true;
      void this.tryResume();
      return;
    }
    if (this.followedLink) return;
    const code = roomCodeFromSearch(window.location.search);
    if (!code) return;
    this.followedLink = true;
    void this.openCode(code);
  }

  private async tryResume(): Promise<void> {
    const player = this.player();
    const lobby = this.lobbyApi();
    if (!player || !lobby || this.handlers.inMatch()) {
      this.followLink();
      return;
    }
    const seat = (await this.resolveResumeSeat()) ?? null;
    if (!seat) {
      this.followLink();
      return;
    }
    try {
      const room = await lobby.roomById(seat.supabaseRoomId);
      const camp = room ? campFor(room, player.id) : null;
      if (!room || room.status !== 'playing' || camp !== seat.camp) {
        clearResumeSeat();
        this.followLink();
        return;
      }
      this.room = room;
      this.matchStarted = true;
      this.handlers.onStartMatch(room, camp, lobby, seat.token);
    } catch {
      // Keep the seat: roomById can fail on a blip; token still valid within the hold.
      this.hint.textContent = 'Could not resume the match. Reload while the seat is still held.';
      this.followLink();
    }
  }

  /** localStorage first; match-server /resume for another browser on the same player. */
  private async resolveResumeSeat(): Promise<ResumeSeat | null> {
    const local = loadResumeSeat();
    if (local) return local;
    const access = await this.lobbyApi()?.accessToken();
    if (!access) return null;
    return fetchServerResumeSeat(readColyseusUrl(), access);
  }

  private async refreshLists(): Promise<void> {
    const lobby = this.lobbyApi();
    if (!lobby) return;
    try {
      if (this.account && this.service) {
        const accountId = this.account.id;
        const [rooms, matches, profile] = await Promise.all([
          this.service.listOpenRooms(),
          this.service.myMatches(accountId),
          this.service.reloadAccount(),
        ]);
        if (profile) this.account = profile;
        this.paintRooms(rooms);
        this.paintResults(matches);
        this.paintAccount();
        return;
      }
      this.paintRooms(await lobby.listOpenRooms());
    } catch (error) {
      this.hint.textContent = onlineError(error);
    }
  }

  private showAuthPane(pane: 'signin' | 'signup'): void {
    this.signinPane.hidden = pane !== 'signin';
    this.signupPane.hidden = pane !== 'signup';
    this.signinError.textContent = '';
    this.signupError.textContent = '';
  }

  /** Checks the typed name, then shows the guest pop-up before anything is kept. */
  private pickGuestName(): void {
    const name = sanitizeVisitorName(this.guestNameInput.value);
    if (!name) {
      this.guestError.textContent = 'Use 2–20 letters, numbers or spaces for the name.';
      return;
    }
    this.guestError.textContent = '';
    this.pendingGuestName = name;
    this.guestNoticeName.textContent = name;
    if (!this.guestNotice.open) this.guestNotice.showModal();
  }

  /** "Play as guest" in the pop-up: keep the name on this device and go online. */
  private async playAsGuest(): Promise<void> {
    const name = this.pendingGuestName;
    this.guestNotice.close();
    if (!name) return;
    let visitor: Visitor | null;
    try {
      visitor = await createVisitor(name, this.visitor);
    } catch {
      // crypto.subtle only exists on https (or localhost).
      this.guestError.textContent = 'Guest play needs the https site in this browser.';
      return;
    }
    if (!visitor) return;
    this.pendingGuestName = null;
    this.visitor = visitor;
    saveVisitor(visitor);
    this.visitorLobby = new VisitorLobby(readColyseusUrl(), visitor);
    this.paintAccount();
    this.hint.textContent = this.account ? ROOM_HINT : GUEST_HINT;
    this.handlers.showMulti();
    void this.refreshLists();
    this.followLink();
  }

  /** "Create an account" in the pop-up: the sign-up form, with the name when it fits. */
  private signUpInstead(): void {
    this.guestNotice.close();
    this.showAuthPane('signup');
    const name = this.pendingGuestName ? sanitizeDisplayName(this.pendingGuestName) : null;
    if (name && !this.nameInput.value) this.nameInput.value = name;
    this.emailInput.focus();
  }

  private async signup(): Promise<void> {
    if (!this.service) {
      this.signupError.textContent = 'Add your Supabase project in Settings first.';
      return;
    }
    const displayName = sanitizeDisplayName(this.nameInput.value);
    const email = this.emailInput.value.trim();
    const password = this.passwordInput.value;
    if (!displayName) {
      this.signupError.textContent = 'Use 2–16 letters, numbers or spaces for the name.';
      return;
    }
    if (!looksLikeEmail(email)) {
      this.signupError.textContent = 'Enter a real email address.';
      return;
    }
    if (password.length < 6) {
      this.signupError.textContent = 'Use at least 6 characters for the password.';
      return;
    }
    this.signupError.textContent = 'Creating the account…';
    try {
      const result = await this.service.signUp(email, password, displayName);
      if (result.pendingEmail || !result.account) {
        this.loginIdInput.value = email;
        this.showAuthPane('signin');
        this.signinError.textContent = 'Account created. Confirm the email, then sign in.';
        return;
      }
      this.account = result.account;
      this.paintAccount();
      this.signupError.textContent = '';
      this.passwordInput.value = '';
      this.presentAccountScreen();
    } catch (error) {
      this.signupError.textContent = onlineError(error);
    }
  }

  private async login(): Promise<void> {
    if (!this.service) {
      this.signinError.textContent = 'Add your Supabase project in Settings first.';
      return;
    }
    const email = this.loginIdInput.value.trim();
    const password = this.loginPasswordInput.value;
    if (!looksLikeEmail(email) || password.length < 6) {
      this.signinError.textContent = 'Enter your email and the password.';
      return;
    }
    this.signinError.textContent = 'Signing in…';
    try {
      this.account = await this.service.signIn(email, password);
      this.paintAccount();
      this.signinError.textContent = '';
      this.loginPasswordInput.value = '';
      this.presentAccountScreen();
    } catch (error) {
      this.signinError.textContent = onlineError(error);
    }
  }

  /** Back to the home screen; a visitor kept on this device plays again. */
  private async logout(): Promise<void> {
    this.account = null;
    this.paintAccount();
    this.paintRooms([]);
    this.paintResults([]);
    this.hint.textContent = this.visitor ? GUEST_HINT : ROOM_HINT;
    this.handlers.showMenu();
    if (this.visitor) void this.refreshLists();
    try {
      await this.service?.signOut();
    } catch (error) {
      this.signinError.textContent = onlineError(error);
    }
  }

  private async create(): Promise<void> {
    const lobby = this.lobbyApi();
    if (!this.ready() || !lobby) return;
    this.hint.textContent = 'Creating a room…';
    try {
      if (await this.openActivePlayingRoom()) return;
      const room = await lobby.createRoom();
      this.enterLobby(room);
    } catch (error) {
      this.hint.textContent = onlineError(error);
    }
  }

  private async find(): Promise<void> {
    const lobby = this.lobbyApi();
    const player = this.player();
    if (!this.ready() || !lobby || !player) return;
    this.hint.textContent = 'Looking for an open room…';
    try {
      if (await this.openActivePlayingRoom()) return;
      const open = (await lobby.listOpenRooms()).filter(
        (room) => room.hostId !== player.id && room.guestId === null,
      );
      // An account plays ranked when it can: rooms held by an account first.
      const pick = (player.visitor ? undefined : open.find((room) => !room.hostVisitor)) ?? open[0];
      if (pick) {
        await this.join(pick.id);
        return;
      }
      await this.create();
    } catch (error) {
      this.hint.textContent = onlineError(error);
    }
  }

  /**
   * If this player already has a `playing` lobby row:
   * resumable → reconnect; active → stay blocked (or reclaim with local token);
   * unknown → retry / force-leave after a second attempt; stale → leave_room.
   */
  private async openActivePlayingRoom(): Promise<boolean> {
    const lobby = this.lobbyApi();
    const player = this.player();
    if (!lobby || !player) return false;
    const playing = await lobby.activePlayingRoom(player.id);
    if (!playing) {
      this.unknownSeatAttempts = 0;
      return false;
    }
    const camp = campFor(playing, player.id);
    if (!camp) return false;
    const access = await lobby.accessToken();
    if (!access) {
      this.bounceActiveMatch();
      return true;
    }
    const localResume = loadResumeSeat();
    const localMatches =
      localResume && localResume.supabaseRoomId === playing.id && localResume.camp === camp;
    const seatStatus = await fetchSeatStatus(readColyseusUrl(), access, playing.id);
    if (seatStatus.status === 'auth') {
      this.unknownSeatAttempts = 0;
      this.hint.textContent = 'Sign in again to resume or leave that match.';
      this.handlers.showGate();
      return true;
    }
    if (seatStatus.status === 'resumable') {
      this.unknownSeatAttempts = 0;
      this.beginMatch(playing, seatStatus.seat.camp, seatStatus.seat.token);
      return true;
    }
    if (seatStatus.status === 'active') {
      this.unknownSeatAttempts = 0;
      // Same player, this tab has the token: reclaim (Colyseus drops the other window).
      if (localMatches && localResume) {
        this.beginMatch(playing, camp, localResume.token);
        return true;
      }
      this.bounceActiveMatch();
      return true;
    }
    if (seatStatus.status === 'unknown') {
      if (localMatches && localResume) {
        this.beginMatch(playing, camp, localResume.token);
        return true;
      }
      this.unknownSeatAttempts += 1;
      if (this.unknownSeatAttempts >= 2) {
        const leave = window.confirm(
          'Match server is still unreachable. Leave the stuck lobby row?\n\nYour opponent may still be in that match.',
        );
        if (!leave) {
          this.bounceUnknownMatch();
          return true;
        }
        try {
          await lobby.leaveRoom(playing.id);
        } catch (error) {
          this.bounceUnknownMatch();
          this.hint.textContent = `${onlineError(error)} Leave did not confirm — try again.`;
          return true;
        }
        clearResumeSeat();
        this.unknownSeatAttempts = 0;
        this.hint.textContent =
          'Left a stuck match (match server was unreachable). Find or create a new room.';
        return false;
      }
      this.bounceUnknownMatch();
      return true;
    }
    // Stale lobby row (match process gone) — clear so Find/Create can proceed.
    this.unknownSeatAttempts = 0;
    try {
      await lobby.leaveRoom(playing.id);
    } catch (error) {
      this.hint.textContent = `${onlineError(error)} Could not clear the old match row.`;
      return true;
    }
    clearResumeSeat();
    return false;
  }

  private async join(roomId: string): Promise<void> {
    const lobby = this.lobbyApi();
    if (!this.ready() || !lobby) return;
    try {
      const room = await lobby.joinRoom(roomId);
      this.enterLobby(room);
    } catch (error) {
      this.hint.textContent = onlineError(error);
      void this.refreshLists();
    }
  }

  private async openCode(code: string): Promise<void> {
    const lobby = this.lobbyApi();
    const player = this.player();
    if (!this.ready() || !lobby || !player) return;
    try {
      const room = await lobby.roomByCode(code);
      if (!room) {
        this.hint.textContent = `No room ${code}.`;
        return;
      }
      if (room.hostId === player.id || room.guestId === player.id) {
        this.enterLobby(room);
        return;
      }
      if (room.status !== 'waiting' || room.guestId) {
        this.hint.textContent = `Room ${code} is not open.`;
        return;
      }
      await this.join(room.id);
    } catch (error) {
      this.hint.textContent = onlineError(error);
    }
  }

  private enterLobby(room: OnlineRoom): void {
    this.room = room;
    this.matchStarted = false;
    this.starting = false;
    this.sawWaiting = room.status === 'waiting';
    this.clearLobbyWatch();
    this.handlers.showLobby();
    this.rememberRoom(room.code);
    this.paintLobby(room);
    if (!this.lobbyApi() || this.matchStarted || this.room?.id !== room.id) return;
    this.watchLobby(room.id);
  }

  private beginMatch(room: OnlineRoom, camp: Camp, resumeToken: string | null): void {
    const lobby = this.lobbyApi();
    if (!lobby || this.matchStarted) return;
    this.matchStarted = true;
    this.clearLobbyWatch();
    try {
      this.handlers.onStartMatch(room, camp, lobby, resumeToken);
    } catch (error) {
      this.matchStarted = false;
      this.lobbyStatus.textContent = onlineError(error);
      this.watchLobby(room.id);
    }
  }

  /** Cold link / other browser: ask the match server for a held reconnection token. */
  private async tryServerResume(room: OnlineRoom, camp: Camp): Promise<void> {
    const lobby = this.lobbyApi();
    if (!lobby || this.matchStarted || this.handlers.inMatch()) {
      this.bounceActiveMatch();
      return;
    }
    // Claim before await so lobby polls do not double-enter.
    this.matchStarted = true;
    this.clearLobbyWatch();
    const access = await lobby.accessToken();
    if (!access) {
      this.matchStarted = false;
      this.bounceActiveMatch();
      return;
    }
    const status = await fetchSeatStatus(readColyseusUrl(), access, room.id);
    if (status.status === 'resumable' && status.seat.camp === camp) {
      try {
        this.handlers.onStartMatch(room, camp, lobby, status.seat.token);
      } catch (error) {
        this.matchStarted = false;
        this.lobbyStatus.textContent = onlineError(error);
        this.watchLobby(room.id);
      }
      return;
    }
    this.matchStarted = false;
    if (status.status === 'auth') {
      this.hint.textContent = 'Sign in again to resume or leave that match.';
      this.handlers.showGate();
      return;
    }
    if (status.status === 'stale') {
      try {
        await lobby.leaveRoom(room.id);
      } catch {
        /* ignore */
      }
      clearResumeSeat();
      this.clearLobbyWatch();
      this.room = null;
      this.sawWaiting = false;
      this.clearRoomQuery();
      if (!this.handlers.inMatch()) this.handlers.showMenu();
      this.hint.textContent = 'That match is no longer running. Find or create a new room.';
      return;
    }
    if (status.status === 'unknown') {
      this.bounceUnknownMatch();
      return;
    }
    this.bounceActiveMatch();
  }

  /** Leave the lobby UI without `leave_room` (must not close a match held elsewhere). */
  private bounceActiveMatch(): void {
    this.clearLobbyWatch();
    this.room = null;
    this.matchStarted = false;
    this.starting = false;
    this.sawWaiting = false;
    this.clearRoomQuery();
    if (!this.handlers.inMatch()) this.handlers.showMenu();
    this.hint.textContent =
      'This match is already running in another window. Keep playing there, or resume within 10 seconds if that window closed.';
  }

  /** Match server unreachable — do not treat as a live seat; Find again may ask to leave. */
  private bounceUnknownMatch(): void {
    this.clearLobbyWatch();
    this.room = null;
    this.matchStarted = false;
    this.starting = false;
    this.sawWaiting = false;
    this.clearRoomQuery();
    if (!this.handlers.inMatch()) this.handlers.showMenu();
    this.hint.textContent =
      'Cannot reach the match server. Find or Create again to retry. A second try will ask before leaving the lobby row.';
  }

  private watchLobby(roomId: string): void {
    this.clearLobbyWatch();
    const lobby = this.lobbyApi();
    if (!lobby) return;
    this.unsubLobby = lobby.watchRoom(roomId, (next) => this.paintLobby(next));
    this.lobbyPoll = window.setInterval(() => {
      void this.pollLobby();
    }, 1000);
  }

  /** Realtime can lag (a visitor has none). A direct read still lets the host see a guest and start. */
  private async pollLobby(): Promise<void> {
    const lobby = this.lobbyApi();
    const roomId = this.room?.id;
    if (!lobby || !roomId || this.matchStarted) return;
    try {
      const next = await lobby.roomById(roomId);
      if (this.matchStarted || this.room?.id !== roomId) return;
      if (!next) {
        this.hint.textContent = 'The room was closed.';
        await this.abandon();
        return;
      }
      this.paintLobby(next);
    } catch (error) {
      if (!this.matchStarted) this.lobbyStatus.textContent = onlineError(error);
    }
  }

  private paintLobby(room: OnlineRoom): void {
    if (this.matchStarted) return;
    this.room = room;
    this.lobbyCode.textContent = room.code;
    if (room.status === 'closed') {
      void this.abandon();
      this.hint.textContent = 'The room was closed.';
      return;
    }
    const player = this.player();
    if (room.status === 'playing' || room.status === 'finished') {
      if (this.matchStarted) return;
      const camp = player ? campFor(room, player.id) : null;
      if (!camp || room.mapId !== 'arena' || room.status === 'finished') {
        this.hint.textContent =
          room.mapId !== 'arena'
            ? 'This room uses a map this client cannot play yet.'
            : room.status === 'finished'
              ? 'This match already finished.'
              : 'This match already started. Open a new room.';
        void this.abandon();
        return;
      }
      if (!this.lobbyApi()) return;
      const resume = loadResumeSeat();
      const entry = resolvePlayingEntry({
        sawWaiting: this.sawWaiting,
        resume,
        roomId: room.id,
        camp,
      });
      if (entry === 'bounce') {
        void this.tryServerResume(room, camp);
        return;
      }
      this.beginMatch(room, camp, entry === 'resume' ? (resume?.token ?? null) : null);
      return;
    }
    this.sawWaiting = true;
    if (room.guestId) {
      const hosting = player?.id === room.hostId;
      const otherName = hosting ? room.guestName : room.hostName;
      const other = otherName
        ? seatName(otherName, hosting ? room.guestVisitor : room.hostVisitor)
        : 'an opponent';
      const unranked = room.hostVisitor || room.guestVisitor ? ' Unranked match.' : '';
      this.lobbyStatus.textContent = `Matched with ${other}.${unranked} Starting…`;
      const lobby = this.lobbyApi();
      if (hosting && !this.starting && lobby) {
        this.starting = true;
        void lobby
          .startRoom(room.id)
          .then((started) => this.paintLobby(started))
          .catch((error: unknown) => {
            this.starting = false;
            this.lobbyStatus.textContent = onlineError(error);
          });
      }
      return;
    }
    this.lobbyStatus.textContent =
      'Waiting for an opponent. They can join from the list, or open your link.';
  }

  private paintAccount(): void {
    if (this.account) {
      this.accountLabel.textContent = `${this.account.displayName} · ${this.account.wins}W ${this.account.losses}L`;
      this.authButton.textContent = 'Log out';
    } else if (this.visitor) {
      this.accountLabel.textContent = `${seatName(this.visitor.name, true)} · scores not saved`;
      this.authButton.textContent = 'Sign in';
    } else if (!this.service) {
      this.accountLabel.textContent = 'Online is off until a Supabase project is set';
      this.authButton.textContent = 'Sign in';
    } else {
      this.accountLabel.textContent = 'Not signed in';
      this.authButton.textContent = 'Sign in';
    }
    const ready = this.lobbyApi() !== null;
    for (const id of ['btn-create-online', 'btn-find-online', 'btn-join-code']) {
      const button = document.getElementById(id);
      if (button instanceof HTMLButtonElement) button.disabled = !ready;
    }
    const code = document.getElementById('join-code');
    if (code instanceof HTMLInputElement) code.disabled = !ready;
  }

  private paintRooms(rooms: OnlineRoom[]): void {
    const root = document.getElementById('room-browser');
    if (!root) return;
    root.replaceChildren();
    const player = this.player();
    if (!player) {
      const note = document.createElement('p');
      note.className = 'panel-note';
      note.textContent = 'Pick a name or sign in to see open rooms.';
      root.append(note);
      return;
    }
    if (rooms.length === 0) {
      const note = document.createElement('p');
      note.className = 'panel-note';
      note.textContent = 'No open rooms. Create one and wait for someone to join.';
      root.append(note);
      return;
    }
    for (const room of rooms) {
      const mine = room.hostId === player.id;
      const host = seatName(room.hostName, room.hostVisitor);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'room-row';
      button.disabled = mine;
      const title = document.createElement('strong');
      title.textContent = room.code;
      const copy = document.createElement('span');
      copy.textContent = mine ? `${host} · your room` : `Join ${host}`;
      button.append(title, copy);
      if (!mine) button.addEventListener('click', () => void this.join(room.id));
      root.append(button);
    }
  }

  private paintResults(matches: readonly MyMatch[]): void {
    const root = document.getElementById('ladder');
    if (!root) return;
    root.replaceChildren();
    if (!this.account) {
      const item = document.createElement('li');
      item.className = 'is-empty';
      item.textContent = 'Sign in to keep your multiplayer results.';
      root.append(item);
      return;
    }
    if (matches.length === 0) {
      const item = document.createElement('li');
      item.className = 'is-empty';
      item.textContent = 'No multiplayer results yet.';
      root.append(item);
      return;
    }
    for (const match of matches) {
      const item = document.createElement('li');
      item.classList.add(match.won ? 'is-win' : 'is-loss');
      const mark = document.createElement('span');
      mark.textContent = match.won ? 'Win' : 'Loss';
      const name = document.createElement('span');
      name.textContent = match.opponent;
      const score = document.createElement('span');
      score.textContent = `${match.mine}–${match.theirs}`;
      item.append(mark, name, score);
      root.append(item);
    }
  }

  private player(): Player | null {
    if (this.account) return { id: this.account.id, visitor: false };
    if (this.visitor) return { id: this.visitor.id, visitor: true };
    return null;
  }

  /** Supabase for an account; the match server for a visitor. */
  private lobbyApi(): LobbyApi | null {
    if (this.account) return this.service;
    return this.visitorLobby;
  }

  private ready(): boolean {
    if (this.lobbyApi()) return true;
    this.hint.textContent = 'Pick a name or sign in to play online.';
    this.askForName();
    return false;
  }

  private async copyLink(): Promise<void> {
    if (!this.room) return;
    const url = roomLink(window.location.href, this.room.code);
    try {
      await navigator.clipboard.writeText(url);
      this.lobbyStatus.textContent = 'Link copied. Send it to your opponent.';
    } catch {
      this.lobbyStatus.textContent = `Share this code: ${this.room.code}`;
    }
  }

  private rememberRoom(code: string): void {
    window.history.replaceState(null, '', roomLink(window.location.href, code));
  }

  private clearRoomQuery(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('room');
    url.hash = '';
    window.history.replaceState(null, '', url);
  }

  private clearLobbyWatch(): void {
    this.unsubLobby?.();
    this.unsubLobby = null;
    if (this.lobbyPoll) window.clearInterval(this.lobbyPoll);
    this.lobbyPoll = 0;
  }

  private stopPoll(): void {
    if (this.poll) window.clearInterval(this.poll);
    this.poll = 0;
  }

  private presentAccountScreen(): void {
    if (this.player()) {
      void this.refreshLists();
      this.handlers.showMenu();
      return;
    }
    this.handlers.showGate();
  }
}
